import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { ArrowLeft, Download, Search, CheckCircle2, XCircle, Trash2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import Header from '@/components/Header';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CourierExport = () => {
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const [rawIds, setRawIds] = useState('');
  const [rows, setRows] = useState([]);
  const [foundCount, setFoundCount] = useState(0);
  const [notFoundCount, setNotFoundCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const parsedIds = rawIds
    .split(/[\n,\s]+/)
    .map(s => s.trim())
    .filter(Boolean);

  const handleFetch = async () => {
    const ids = parsedIds;
    if (ids.length === 0) {
      toast.error('Please scan or paste at least one ID');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/courier-export/lookup`, { ids }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRows(res.data.rows || []);
      setFoundCount(res.data.found || 0);
      setNotFoundCount(res.data.not_found || 0);
      setHasSearched(true);
      if (res.data.not_found > 0) {
        toast.warning(`${res.data.found} found · ${res.data.not_found} not found`);
      } else {
        toast.success(`All ${res.data.found} IDs found`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = () => {
    const foundRows = rows.filter(r => r.found);
    if (foundRows.length === 0) {
      toast.error('No matched rows to export');
      return;
    }
    // New column layout requested by user:
    // S.No. | Member ID | Name | Mobile Number | District | State | Pincode
    const headers = ['S.No.', 'Member ID', 'Name', 'Mobile Number', 'District', 'State', 'Pincode'];
    const data = foundRows.map((r, idx) => [
      idx + 1,
      r.reference_no,
      r.consignee_name,
      r.mobile_number,
      r.district || '',
      r.state || '',
      r.pincode,
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    // Auto-width for readability
    ws['!cols'] = [
      { wch: 6 },   // S.No.
      { wch: 14 },  // Member ID
      { wch: 28 },  // Name
      { wch: 20 },  // Mobile Number
      { wch: 18 },  // District
      { wch: 18 },  // State
      { wch: 10 },  // Pincode
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Courier Export');
    const stamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Courier_Export_${stamp}.xlsx`);
    toast.success(`Downloaded ${foundRows.length} rows`);
  };

  const handleClear = () => {
    setRawIds('');
    setRows([]);
    setFoundCount(0);
    setNotFoundCount(0);
    setHasSearched(false);
    textareaRef.current?.focus();
  };

  const handleRemoveNotFound = () => {
    const remaining = rawIds
      .split(/[\n,\s]+/)
      .map(s => s.trim())
      .filter(Boolean);
    const notFoundSet = new Set(
      rows.filter(r => !r.found).map(r => r.reference_no.toUpperCase())
    );
    const kept = remaining.filter(id => !notFoundSet.has(id.toUpperCase()));
    setRawIds(kept.join('\n'));
    toast.info(`Removed ${notFoundSet.size} not-found IDs from input`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Package className="h-7 w-7 text-blue-700" />
              Courier Export
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Scan or paste Member / Advertiser IDs. Download an Excel matching your courier partner&apos;s template.
            </p>
          </div>
        </div>

        {/* Input Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Scan IDs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Scan or paste IDs (one per line — barcode scanner works too)</Label>
              <Textarea
                ref={textareaRef}
                value={rawIds}
                onChange={(e) => setRawIds(e.target.value)}
                placeholder={"ATML1144\nATML1147\nATAD501\n..."}
                rows={8}
                className="font-mono text-sm"
                autoFocus
                data-testid="ids-textarea"
              />
              <p className="text-xs text-gray-500">
                {parsedIds.length > 0
                  ? `${parsedIds.length} ID${parsedIds.length === 1 ? '' : 's'} ready`
                  : 'Each scan adds a new line automatically. Any whitespace or commas also work as separators.'}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleFetch}
                disabled={loading || parsedIds.length === 0}
                className="bg-blue-700 hover:bg-blue-800"
                data-testid="fetch-btn"
              >
                <Search className="mr-2 h-4 w-4" />
                {loading ? 'Fetching…' : `Fetch ${parsedIds.length || ''} Details`}
              </Button>
              <Button
                variant="outline"
                onClick={handleClear}
                disabled={loading}
                data-testid="clear-btn"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {hasSearched && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Results</CardTitle>
                <div className="flex gap-2 mt-2">
                  <Badge className="bg-green-600">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    {foundCount} Found
                  </Badge>
                  {notFoundCount > 0 && (
                    <Badge className="bg-red-600">
                      <XCircle className="mr-1 h-3 w-3" />
                      {notFoundCount} Not Found
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {notFoundCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveNotFound}
                    data-testid="remove-not-found-btn"
                  >
                    Remove Not-Found from Input
                  </Button>
                )}
                <Button
                  onClick={handleDownloadExcel}
                  disabled={foundCount === 0}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="download-excel-btn"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Excel ({foundCount})
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-left">
                    <tr>
                      <th className="p-2 border-b">#</th>
                      <th className="p-2 border-b">Reference No</th>
                      <th className="p-2 border-b">Consignee Name</th>
                      <th className="p-2 border-b">Full Address</th>
                      <th className="p-2 border-b">Pincode</th>
                      <th className="p-2 border-b">Mobile</th>
                      <th className="p-2 border-b">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr
                        key={`${r.reference_no}-${idx}`}
                        data-testid={`row-${r.reference_no}`}
                        className={r.found ? 'hover:bg-gray-50' : 'bg-red-50 hover:bg-red-100'}
                      >
                        <td className="p-2 border-b text-gray-500">{idx + 1}</td>
                        <td className="p-2 border-b font-mono font-semibold" data-testid={`cell-ref-${r.reference_no}`}>{r.reference_no}</td>
                        <td className="p-2 border-b" data-testid={`cell-name-${r.reference_no}`}>{r.consignee_name || <span className="text-gray-400">—</span>}</td>
                        <td className="p-2 border-b text-xs" data-testid={`cell-address-${r.reference_no}`}>{r.full_address || <span className="text-gray-400">—</span>}</td>
                        <td className="p-2 border-b font-mono" data-testid={`cell-pincode-${r.reference_no}`}>{r.pincode || <span className="text-gray-400">—</span>}</td>
                        <td className="p-2 border-b font-mono" data-testid={`cell-mobile-${r.reference_no}`}>{r.mobile_number || <span className="text-gray-400">—</span>}</td>
                        <td className="p-2 border-b">
                          {r.found ? (
                            <Badge variant="outline" className="text-green-700 border-green-400">
                              {r.type}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-red-700 border-red-400">
                              Not Found
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CourierExport;
